import React, { useEffect, useState } from 'react';
import { Box, Button, Card, CardContent, Grid, Typography, Skeleton } from '@mui/material';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { LoadingButton } from '@mui/lab';
import FormData from 'form-data';
import { useSnackbar } from 'notistack';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { styles } from '../styles';
import TextField from '../../../../components/common/InputField/TextField';
import DragAndDrop from '../../../../components/common/DragAndDrop';
import {
    useModifyRoleMutation,
    useCreateRoleMutation,
    useSaveRoleMutation,
    useModifyRoleActionMutation,
} from '../../../../api/roles';

const AddEditRoles = (props) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { id } = useParams();
    const [targetKeys, setTargetKeys] = useState([]);

    const [preCreateUpdateDetails, setPreCreateUpdateDetails] = useState([]);
    const [columns, setColumns] = useState(null);
    const [name, setName] = useState('');
    const { enqueueSnackbar } = useSnackbar();
    const [grantedPermissions, setGrantedPermissions] = useState([]);

    const [modifyRole, { isLoading: isModifyLoading }] = useModifyRoleMutation();
    const [createRole, { isLoading: isCreateLoading }] = useCreateRoleMutation();
    const [saveRole, { isLoading: isSaveLoading }] = useSaveRoleMutation();
    const [modifyRoleAction, { isLoading: isModifyActionLoading }] = useModifyRoleActionMutation();

    const isPreCreationLoading = isModifyLoading || isCreateLoading;
    const isLoading = isSaveLoading || isModifyActionLoading;

    const isEditMode = location.pathname.includes('edit');

    useEffect(() => {
        const fetchPreCreateUpdateData = async () => {
            const bodyFormData = new FormData();
            isEditMode && bodyFormData.append('id', id);
            const url = isEditMode ? modifyRole : createRole;
            try {
                const response = await url(bodyFormData).unwrap();
                if (!response) return;
                if (response?.status === 'ERROR') throw new Error(response?.message);
                const { permissionsList, grantedPermissions = [], secRoleInstance = {} } = response;
                const availableRoles = permissionsList.map(({ id, description, name }) => ({
                    id,
                    key: name,
                    status: 'available',
                    title: description,
                    isSelected: false,
                    value: name,
                }));
                setName(secRoleInstance?.displayName || '');
                setGrantedPermissions(grantedPermissions);
                setPreCreateUpdateDetails(availableRoles);
            } catch (err) {
                if (err?.statusCode === 400) enqueueSnackbar(err?.data?.message, { variant: 'error' });
                else enqueueSnackbar(err?.message || 'Something went wrong!', { variant: 'error' });
            }
        };

        fetchPreCreateUpdateData();
    }, [id]);

    useEffect(() => {
        if (isEditMode && grantedPermissions?.length && preCreateUpdateDetails?.length) {
            const grantedIds = grantedPermissions.map(
                (permission) => preCreateUpdateDetails.find((element) => element.key === permission)?.key,
            );
            setTargetKeys(grantedIds);
        }
    }, [isEditMode, grantedPermissions, preCreateUpdateDetails]);

    const createRoleHandler = async () => {
        const bodyFormData = new FormData();
        const grantedValues = targetKeys.join(',');
        bodyFormData.append('displayName', name);
        bodyFormData.append('grantedPermissions', grantedValues);
        try {
            const response = await saveRole(bodyFormData).unwrap();
            if (!response) return;
            if (response?.status === 'ERROR') throw new Error(response?.message);
            enqueueSnackbar('Role created successfuly', { variant: 'success' });
            navigate('/settings/roles');
        } catch (error) {
            if (error?.statusCode === 400) enqueueSnackbar(error?.data?.message, { variant: 'error' });
            else enqueueSnackbar(error?.message || 'Something went wrong!', { variant: 'error' });
        }
    };

    const modifyRoleActionHandler = async () => {
        const bodyFormData = new FormData();
        const grantedValues = targetKeys.join(',');
        bodyFormData.append('displayName', name);
        bodyFormData.append('grantedPermissions', grantedValues);
        bodyFormData.append('id', id);
        try {
            const response = await modifyRoleAction(bodyFormData).unwrap();
            if (!response) return;
            if (response?.status === 'ERROR') throw new Error(response?.message);
            enqueueSnackbar('Role updated successfuly', { variant: 'success' });
            navigate('/settings/roles');
        } catch (error) {
            if (error?.statusCode === 400) enqueueSnackbar(error?.data?.message, { variant: 'error' });
            else enqueueSnackbar(error?.message || 'Something went wrong!', { variant: 'error' });
        }
    };

    return (
        <Box>
            <Box sx={styles.contentHeaderWrapper}>
                <Typography variant="h4">Roles</Typography>
            </Box>
            <Box sx={styles.contentBodyWrapper}>
                <Card>
                    <CardContent>
                        <Grid container direction="column" spacing={2}>
                            <Grid item>
                                <Button
                                    startIcon={<ChevronLeftIcon />}
                                    variant="text"
                                    color="secondary"
                                    onClick={() => navigate('/settings/roles')}
                                >
                                    Back
                                </Button>
                            </Grid>
                            <Grid item>
                                <Typography variant="h4">{!isEditMode ? 'Add New Role' : 'Modify Role'}</Typography>
                            </Grid>
                            <Grid item xs={4}>
                                <TextField
                                    label="Role Name"
                                    name="name"
                                    onChange={(event) => setName(event.target.value)}
                                    error={name?.length <= 0}
                                    helperText={name?.length <= 0 && 'Role Name is required'}
                                    value={name}
                                />
                            </Grid>
                            <Grid item>
                                {isPreCreationLoading ? (
                                    <Box>
                                        <Skeleton height={42} />
                                        <Skeleton />
                                        <Skeleton />
                                    </Box>
                                ) : (
                                    <DragAndDrop
                                        allorders={preCreateUpdateDetails}
                                        columns={columns}
                                        setColumns={setColumns}
                                        grantedPermissions={grantedPermissions}
                                        targetKeys={targetKeys}
                                        setTargetKeys={setTargetKeys}
                                    />
                                )}
                            </Grid>
                            <Grid
                                item
                                sx={{
                                    pt: '0 !important',
                                    display: 'flex',
                                }}
                            >
                                <Box
                                    sx={{
                                        flexGrow: 1,
                                    }}
                                />
                                <LoadingButton
                                    loading={isLoading}
                                    size="medium"
                                    sx={{
                                        ml: 2,
                                    }}
                                    variant="contained"
                                    color="primary"
                                    disabled={name?.length <= 0}
                                    onClick={!isEditMode ? createRoleHandler : modifyRoleActionHandler}
                                >
                                    {isEditMode ? 'Update' : 'Create'}
                                </LoadingButton>
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>
            </Box>
        </Box>
    );
};

export default AddEditRoles;
